import React from "react";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";
import { ReceiptQrCodeGuestManagementCallout } from "@qgiv/core-donor";
import { constants, getSingularPluralWordForm } from "@qgiv/core-js";
import { selectReceipt } from "../../../redux/slices/receiptSlice";
import { selectHTTP, selectIsCms } from "../../../redux/slices/configSlice";
import { selectCmsControlByIdByPage } from "../../../redux/slices/cmsSettingsSlice";
import { selectAllP2pSettings } from "../../../redux/slices/p2pSettingsSlice";

const ConnectedReceiptQrCodeGuestManagementCallout = ({
    controlId,
    isMediumScreen = false,
}) => {
    const {
        ENUMS: { DefaultView },
    } = constants;

    const p2pSettings = useSelector(selectAllP2pSettings);
    const receipt = useSelector(selectReceipt);
    const {
        transaction: {
            email,
            qrCodeUrl,
            qrCodeCount,
            allQrCodesGenerated,
            numberOfTicketEmailsToSend,
        },
    } = receipt;

    const HTTP_HOST = useSelector(selectHTTP);

    const control = useSelector((state) =>
        selectCmsControlByIdByPage(
            controlId,
            DefaultView.STANDARD_REGISTRATION_RECEIPT,
            state,
        ),
    );

    const {
        options: {
            viewTicketsLink = "View Ticket(s)",
            ticketConfirmationMessage = "Ticket(s) will be emailed to %EmailAddress%",
        },
    } = control;

    const handlePluralViewTicketsButtonLabel = getSingularPluralWordForm(
        viewTicketsLink,
        qrCodeCount,
    );

    const handledPluralTicketConfirmationMessage = getSingularPluralWordForm(
        ticketConfirmationMessage,
        qrCodeCount,
    );

    const countOfParticipantsEmailedExcludingPurchaser =
        numberOfTicketEmailsToSend - 1;

    const handledPluralEmailedTicketCount = ` and ${countOfParticipantsEmailedExcludingPurchaser} ${getSingularPluralWordForm(
        "other(s)",
        countOfParticipantsEmailedExcludingPurchaser,
    )} `;

    const ticketConfirmationMessageArray =
        handledPluralTicketConfirmationMessage.split("%EmailAddress%");

    // This is the parent-level setting, officially coded as 'enableCheckIn' elsewhere.
    // We save it as 'enableQRCodeCheckIn' here, to make clear the
    // connection to QR codes, specifically.
    const enableQRCodeCheckIn = p2pSettings.enableCheckIn === 1;

    const isCms = useSelector(selectIsCms);

    const hasQrCodesAvailable =
        (enableQRCodeCheckIn && !!qrCodeUrl?.length > 0 && qrCodeCount > 0) ||
        (enableQRCodeCheckIn && isCms);

    const qrCodeUrlComplete = `${HTTP_HOST}${qrCodeUrl}`;
    const includeManagedEmailCount =
        countOfParticipantsEmailedExcludingPurchaser > 0;

    const receiptQrCodeGuestManagementCalloutProps = {
        allQrCodesGenerated,
        email,
        handledPluralEmailedTicketCount,
        handlePluralManageTicketsLinkLabel: "",
        handlePluralViewTicketsButtonLabel,
        hasGuestManagementAvailable: false,
        hasQrCodesAvailable,
        includeManagedEmailCount,
        isMediumScreen,
        manageTicketsUrlComplete: "",
        numberOfQrCodes: qrCodeCount,
        qrCodeUrlComplete,
        ticketConfirmationMessageArray,
    };

    return (
        <ReceiptQrCodeGuestManagementCallout
            {...receiptQrCodeGuestManagementCalloutProps}
        />
    );
};

ConnectedReceiptQrCodeGuestManagementCallout.propTypes = {
    controlId: PropTypes.number.isRequired,
    isMediumScreen: PropTypes.bool,
};

export default ConnectedReceiptQrCodeGuestManagementCallout;
